import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForecastMap } from './ForecastMapContext';

// Basemap ids must match the raster layers defined in ForecastMap's baseStyle.
// 'default' = our OpenTopoMap (the app's original basemap).
export const BASEMAPS = ['topo', 'sat', 'terrain', 'relief'];
const STORAGE_KEY = 'forecast-basemap';

export const savedBasemap = () => {
    const v = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY);
    if (v === 'default') return 'topo'; // pre-rename saved value
    return BASEMAPS.includes(v) ? v : 'topo';
};

// Skysight-style basemap picker (bottom-left): collapsed it shows ONE preview
// tile of the active style; clicking expands the 4 options; picking one swaps
// the raster layer visibility (overlays/wind layers above are untouched).
// Each tile's preview is a real map tile of that style (fixed Balkans tile).
const PREVIEWS = {
    topo: 'https://tile.opentopomap.org/7/71/46.png',
    sat: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/7/46/71',
    terrain: 'https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/7/46/71',
    // hypsometric ramp swatch (no public colour-relief tile to thumbnail)
    relief: null,
};
const RELIEF_PREVIEW = 'linear-gradient(135deg, #3b82f6 0%, #4ade80 35%, #facc15 65%, #ef4444 100%)';

const BasemapSwitcher = ({ active, onChange }) => {
    const { t } = useTranslation();
    const map = useForecastMap();
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);

    // Collapse with the slide-out animation, then unmount the option tiles.
    const close = () => {
        setClosing(true);
        setTimeout(() => { setOpen(false); setClosing(false); }, 230);
    };

    const select = (id) => {
        close();
        if (!map || id === active) return;
        // A basemap may consist of several layers (terrain = bg + hillshade) —
        // toggle every style layer whose id starts with `basemap-{id}`.
        map.getStyle().layers.forEach((l) => {
            if (!l.id.startsWith('basemap-')) return;
            const mine = l.id === `basemap-${id}` || l.id.startsWith(`basemap-${id}-`);
            map.setLayoutProperty(l.id, 'visibility', mine ? 'visible' : 'none');
        });
        localStorage.setItem(STORAGE_KEY, id);
        onChange(id);
    };

    const LABELS = { topo: 'Topo', sat: 'Sat', terrain: t('Terrain'), relief: t('Relief') };

    const tile = (b, onClick, ringClass, i = null) => (
        <button
            key={b}
            type="button"
            aria-label={LABELS[b]}
            onClick={onClick}
            className={`relative w-11 h-11 shrink-0 rounded-md overflow-hidden transition-all ${i !== null ? (closing ? 'basemap-option-out' : 'basemap-option') : ''} ${ringClass}`}
            style={{ backgroundImage: PREVIEWS[b] ? `url(${PREVIEWS[b]})` : RELIEF_PREVIEW, backgroundSize: 'cover', backgroundPosition: 'center', ...(i !== null ? { '--i': i } : {}) }}
        >
            <span className="absolute inset-x-0 bottom-0 pt-3 pb-0.5 text-[9px] font-semibold text-white text-center bg-gradient-to-t from-black/70 to-transparent leading-none">
                {LABELS[b]}
            </span>
        </button>
    );

    return (
        <div
            // animate the glass box width along with the tiles: 56px collapsed
            // (one 44px tile + padding), 206px expanded (4 tiles + gaps).
            className="absolute bottom-7 left-2 sm:bottom-8 sm:left-3 z-[5] flex gap-1.5 p-1.5 rounded-lg bg-slate-900/60 backdrop-blur-md ring-1 ring-white/10 shadow overflow-hidden transition-[max-width] duration-200 ease-out"
            style={{ maxWidth: open && !closing ? 206 : 56 }}
        >
            {open ? (
                <>
                    {/* anchor = current selection, stays put; others slide from it */}
                    {tile(active, close, 'ring-2 ring-blue-500')}
                    {BASEMAPS.filter((b) => b !== active).map((b, i) => tile(b, () => select(b),
                        'ring-1 ring-white/20 opacity-80 hover:opacity-100', i))}
                </>
            ) : tile(active, () => setOpen(true), 'ring-1 ring-white/20 hover:ring-blue-400')}
        </div>
    );
};

export default BasemapSwitcher;
